import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, type ReactNode } from 'react';
import PublicLayout from './components/PublicLayout';
import Home from './pages/Home';
import Services from './pages/Services';
import CustomWork from './pages/CustomWork';
import Gallery from './pages/Gallery';
import Booking from './pages/Booking';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Account from './pages/Account';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import Dashboard from './pages/admin/Dashboard';
import BookingsAdmin from './pages/admin/BookingsAdmin';
import PackagesAdmin from './pages/admin/PackagesAdmin';
import ServicesAdmin from './pages/admin/ServicesAdmin';
import QuotesAdmin from './pages/admin/QuotesAdmin';
import GalleryAdmin from './pages/admin/GalleryAdmin';
import TestimonialsAdmin from './pages/admin/TestimonialsAdmin';
import TeamAdmin from './pages/admin/TeamAdmin';
import SettingsAdmin from './pages/admin/SettingsAdmin';
import { useAuth } from './context/AuthContext';

function ScrollToTop() {
  const { pathname } = useLocation();
  // 'instant' bypasses the global smooth-scroll CSS — animating from a deep
  // scroll position on route change looks like the new page failed to load.
  // Braces matter: browser extensions can patch window.scrollTo to return a
  // value, and returning that to React makes it call it as an effect cleanup.
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    } catch {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
}

/** Cleaners only get the bookings screen — everything else is admin-only. */
function AdminOnly({ children }: { children: ReactNode }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin ? <>{children}</> : <Navigate to="/admin/bookings" replace />;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/custom" element={<CustomWork />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/booking" element={<Booking />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Home />} />
        </Route>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminOnly><Dashboard /></AdminOnly>} />
          <Route path="bookings" element={<BookingsAdmin />} />
          <Route path="packages" element={<AdminOnly><PackagesAdmin /></AdminOnly>} />
          <Route path="services" element={<AdminOnly><ServicesAdmin /></AdminOnly>} />
          <Route path="quotes" element={<AdminOnly><QuotesAdmin /></AdminOnly>} />
          <Route path="gallery" element={<AdminOnly><GalleryAdmin /></AdminOnly>} />
          <Route path="testimonials" element={<AdminOnly><TestimonialsAdmin /></AdminOnly>} />
          <Route path="team" element={<AdminOnly><TeamAdmin /></AdminOnly>} />
          <Route path="settings" element={<AdminOnly><SettingsAdmin /></AdminOnly>} />
        </Route>
      </Routes>
    </>
  );
}
